import { TRequestDiagnostics } from './types';

const isBrowser = () => typeof window !== 'undefined' && typeof document !== 'undefined';

let restoredFromBFCache = false;
let restoredAt: number | null = null;
let hiddenTime = 0;
let hiddenSince: number | null = null;
let visibleSince: number | null = null;
let successCount = 0;
let errorCount = 0;
let lastSuccessAt: number | null = null;
let wsReconnectCount = 0;
let broadcastUnauthorizeReceived = false;
let initialIsAuthenticated: boolean | null = null;
let appStartedAt: number | null = null;

/**
 * Возвращает запись о навигации, которой открыта страница
 */
function getNavigationEntry(): PerformanceNavigationTiming | null {
  if (!isBrowser() || typeof performance === 'undefined' || !performance.getEntriesByType) {
    return null;
  }

  const [navigation] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];

  return navigation || null;
}

/**
 * Документ отдан браузером из кеша: по сети не передано ни байта, а тело при этом есть.
 * Значит серверный рендер не выполнялся и снимок в разметке остался прежним
 */
function isServedFromCache(navigation: PerformanceNavigationTiming | null): boolean | null {
  if (!navigation) {
    return null;
  }

  return navigation.transferSize === 0 && navigation.decodedBodySize > 0;
}

/**
 * Начинает отслеживать восстановление страницы из bfcache и время, проведённое вкладкой в фоне.
 * Вызывается один раз при старте приложения
 */
export function initPageActivityTracking(): void {
  if (!isBrowser()) {
    return;
  }

  appStartedAt = Date.now();
  visibleSince = document.visibilityState === 'visible' ? Date.now() : null;
  hiddenSince = document.visibilityState === 'hidden' ? Date.now() : null;

  window.addEventListener('pageshow', event => {
    if (event.persisted) {
      restoredFromBFCache = true;
      restoredAt = Date.now();
      visibleSince = Date.now();
    }
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      hiddenSince = Date.now();
      visibleSince = null;

      return;
    }

    if (hiddenSince !== null) {
      hiddenTime += Date.now() - hiddenSince;
      hiddenSince = null;
    }

    visibleSince = Date.now();
  });
}

/**
 * Отмечает удачный запрос к публичной ручке чатов
 */
export function trackPublicApiSuccess(): void {
  if (!isBrowser()) {
    return;
  }

  successCount += 1;
  lastSuccessAt = Date.now();
}

/**
 * Отмечает неудачный запрос к публичной ручке чатов и возвращает его порядковый номер
 */
export function trackPublicApiError(): number {
  if (!isBrowser()) {
    return 0;
  }

  errorCount += 1;

  return errorCount;
}

/**
 * Отмечает состояние авторизации, пришедшее из SSR при старте приложения
 */
export function setInitialIsAuthenticated(value: boolean): void {
  initialIsAuthenticated = value;
}

/**
 * Отмечает получение события о разлогине из соседней вкладки.
 * Вызывается до проверки условий обработчика, чтобы видеть и проигнорированные сигналы
 */
export function trackBroadcastUnauthorize(): void {
  broadcastUnauthorizeReceived = true;
}

/**
 * Отмечает попытку переоткрыть WS-соединение
 */
export function trackWsReconnect(): void {
  if (!isBrowser()) {
    return;
  }

  wsReconnectCount += 1;
}

/**
 * Возвращает историю страницы на момент запроса
 */
export function getPageActivity(): TRequestDiagnostics {
  const now = Date.now();
  const navigation = getNavigationEntry();

  return {
    /** Сколько живёт само приложение. Сильно меньше snapshotAge — значит документ пролежал в кеше */
    appAge: appStartedAt === null ? null : now - appStartedAt,
    navigationType: navigation ? navigation.type : null,
    servedFromCache: isServedFromCache(navigation),
    restoredFromBFCache,
    /** Сколько прошло с восстановления страницы из bfcache. Малое значение указывает на причину отказа */
    msSinceRestore: restoredAt === null ? null : now - restoredAt,
    /** Суммарное время вкладки в фоне, мс */
    hiddenTime: hiddenSince === null ? hiddenTime : hiddenTime + (now - hiddenSince),
    msSinceVisible: visibleSince === null ? null : now - visibleSince,
    successCount,
    msSinceLastSuccess: lastSuccessAt === null ? null : now - lastSuccessAt,
    wsReconnectCount,
    broadcastUnauthorizeReceived,
    initialIsAuthenticated,
  };
}
